import React, { useState, useEffect, useRef } from 'react';
import { analytics } from '../../../../services/analytics';
import styles from './FeedBannerSlider.module.scss';

export interface BannerItem {
  title?: string;
  /** API may send subtitle; falls back to description for the subtitle line */
  subtitle?: string;
  description?: string;
  button_text?: string;
  master_image?: string;
  background_image?: string;
  /** Shown at mobile breakpoints when set; falls back to background_image */
  background_image_mobile?: string;
  external_link?: string;
  action?: string;
  dynamic_item_id?: string;
}

function hasNonEmptyText(value?: string | null): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

interface FeedBannerSliderProps {
  /** Banner/promo items from API (no price fields) */
  items: BannerItem[];
}

const AUTO_PLAY_MS = 5500;

const FeedBannerSlider: React.FC<FeedBannerSliderProps> = ({ items }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const showSlide = (index: number) => {
    const newIndex = index < 0 ? items.length - 1 : index >= items.length ? 0 : index;
    setCurrentSlide(newIndex);
  };

  const resumeAutoPlay = () => {
    setIsAutoPlaying(true);
  };

  const startAutoPlay = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (isAutoPlaying && items.length > 1) {
      intervalRef.current = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % items.length);
      }, AUTO_PLAY_MS);
    }
  };

  const stopAutoPlay = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  useEffect(() => {
    if (!items || items.length <= 1) return;
    startAutoPlay();
    return () => stopAutoPlay();
  }, [isAutoPlaying, items?.length]);

  const handleCta = (item: BannerItem) => {
    const link = (item.external_link || '').trim();
    if (!link) return;
    // window.open is invisible to the SDK's outgoing-link auto-tracking.
    analytics.track('banner_cta_clicked', {
      banner: (item.title || item.button_text || '').trim().slice(0, 80) || undefined,
      target: link.slice(0, 200),
    });
    window.open(link, '_blank');
  };

  const goPrev = () => {
    stopAutoPlay();
    showSlide(currentSlide - 1);
    setTimeout(resumeAutoPlay, 3000);
  };

  const goNext = () => {
    stopAutoPlay();
    showSlide(currentSlide + 1);
    setTimeout(resumeAutoPlay, 3000);
  };

  const goTo = (index: number) => {
    stopAutoPlay();
    setCurrentSlide(index);
    setTimeout(resumeAutoPlay, 3000);
  };

  if (!items || items.length === 0) return null;

  return (
    <section className={styles.hero} aria-label="Promotional banner">
      <div className={styles.container}>
        <div className={styles.slider}>
          {items.length > 1 && (
            <>
              <button
                type="button"
                className={`${styles.arrow} ${styles.arrowPrev}`}
                onClick={goPrev}
                aria-label="Previous slide"
              >
                <i className="fas fa-chevron-left" aria-hidden />
              </button>
              <button
                type="button"
                className={`${styles.arrow} ${styles.arrowNext}`}
                onClick={goNext}
                aria-label="Next slide"
              >
                <i className="fas fa-chevron-right" aria-hidden />
              </button>
            </>
          )}

          {items.map((item, index) => {
            const hasTitle = hasNonEmptyText(item.title);
            const subtitleText = [item.subtitle, item.description]
              .find((s) => hasNonEmptyText(s));
            const hasSubtitle = Boolean(subtitleText);
            const hasButton = hasNonEmptyText(item.button_text);
            const hasMasterImage = hasNonEmptyText(item.master_image);
            const hasContent = hasTitle || hasSubtitle || hasButton;
            const externalLinkTrimmed = (item.external_link ?? '').trim();
            const hasExternalLink = externalLinkTrimmed.length > 0;
            const imageOpensLink = hasMasterImage && hasExternalLink;
            /** Background / full-slide hit when there is no master image to wrap */
            const slideOpensLink = hasExternalLink && !hasMasterImage;

            const bgDesktop = hasNonEmptyText(item.background_image);
            const bgMobile = hasNonEmptyText(item.background_image_mobile);

            const slideClassNames = [
              styles.slide,
              index === currentSlide ? styles.active : '',
              !hasMasterImage ? styles.slideNoMedia : '',
              !hasContent && hasMasterImage ? styles.slideMediaOnly : '',
            ]
              .filter(Boolean)
              .join(' ');

            const masterImg = (
              <img
                src={item.master_image}
                alt=""
                className={styles.mediaImg}
                loading={index === 0 ? 'eager' : 'lazy'}
              />
            );

            return (
            <div
              key={index}
              className={slideClassNames}
              role="group"
              aria-roledescription="slide"
              aria-label={`Slide ${index + 1} of ${items.length}`}
              aria-hidden={index !== currentSlide}
            >
              {bgDesktop && bgMobile && (
                <>
                  <div
                    className={`${styles.slideBg} ${styles.slideBgDesktop}`}
                    style={{ backgroundImage: `url(${(item.background_image ?? '').trim()})` }}
                    aria-hidden
                  />
                  <div
                    className={`${styles.slideBg} ${styles.slideBgMobile}`}
                    style={{ backgroundImage: `url(${(item.background_image_mobile ?? '').trim()})` }}
                    aria-hidden
                  />
                </>
              )}
              {bgDesktop && !bgMobile && (
                <div
                  className={styles.slideBg}
                  style={{ backgroundImage: `url(${(item.background_image ?? '').trim()})` }}
                  aria-hidden
                />
              )}
              {!bgDesktop && bgMobile && (
                <div
                  className={styles.slideBg}
                  style={{ backgroundImage: `url(${(item.background_image_mobile ?? '').trim()})` }}
                  aria-hidden
                />
              )}
              {slideOpensLink && (
                <a
                  href={externalLinkTrimmed}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.slideExternalLink}
                  aria-label={
                    hasTitle
                      ? `${(item.title ?? '').trim()} — opens in new tab`
                      : 'Open promotion link in new tab'
                  }
                />
              )}
              {hasContent && (
                <div className={styles.content}>
                  {hasTitle && <h2 className={styles.title}>{item.title}</h2>}
                  {hasSubtitle && (
                    <p className={styles.description}>{subtitleText}</p>
                  )}
                  {hasButton && (
                    <div className={styles.ctaWrap}>
                      <button
                        type="button"
                        className={styles.cta}
                        onClick={() => handleCta(item)}
                      >
                        {(item.button_text ?? '').trim()}
                      </button>
                    </div>
                  )}
                </div>
              )}
              {hasMasterImage && (
                <div className={styles.media}>
                  <div className={styles.mediaInner}>
                    {imageOpensLink ? (
                      <a
                        href={externalLinkTrimmed}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.mediaLink}
                        aria-label={
                          hasTitle
                            ? `${(item.title ?? '').trim()} — opens in new tab`
                            : 'Open link in new tab'
                        }
                      >
                        {masterImg}
                      </a>
                    ) : (
                      masterImg
                    )}
                  </div>
                </div>
              )}
            </div>
            );
          })}

          {items.length > 1 && (
            <div className={styles.footer}>
              {items.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  role="tab"
                  aria-selected={index === currentSlide}
                  aria-label={`Go to slide ${index + 1}`}
                  tabIndex={index === currentSlide ? 0 : -1}
                  className={`${styles.dot} ${index === currentSlide ? styles.active : ''}`}
                  onClick={() => goTo(index)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default FeedBannerSlider;
